<template>
  <div class="d-flex flex-column py-4 px-1 w-100 h-100">
    <div class="d-flex">
      <div class="d-flex me-1">
        <div class="left-icon" />
        <div class="right-icon" />
      </div>
      <p
        class="ma-0 pa-0 blue-grey--text text--darken-2 font-size-10 font-weight-500 mb-3 impact-custom-title"
      >
        {{ impactTitle }}
      </p>
    </div>
    <p class="ma-0 pa-0 blue-grey--text text--darken-3 h6">
      {{ metricText }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import convert from 'convert'
import _ from 'lodash'

export default {
  name: 'Impact',

  props: {
    metric: String,
    total: {
      type: Number,
      required: false,
    },
    unit: {
      type: String,
      default: '',
    },
    system: {
      type: String,
      default: 'metric',
    },
  },

  data() {
    return {
      units: {
        metric: {
          cubic_meter: 'm3',
          gram: 'gr',
          joule: 'joule',
          kilogram: 'kg',
          kilowatt: 'kilowatt',
          kilowatt_hour: 'kilowatt-hour',
          liter: 'l',
          milligram: 'milligram',
          tonne: 'tonne',
          units: 'units',
        },
        imperial: {
          cubic_inch: 'cubic inch',
          gallon: 'gal',
          ounce: 'oz',
          pound: 'lb',
          uk_ton: 'long ton',
          us_ton: 'US ton',
        },
        noSystem: {
          horsepower: 'horsepower',
          complains_per_million: 'complains_per_million',
          number: 'number',
          percentage: 'percentage',
          pieces: 'pieces',
          scale: 'scale',
          unit_pieces: 'unit_pieces',
          calorie: 'calorie',
        },
        time: {
          milliseconds: 'ms',
          seconds: 's',
          minutes: 'min',
          hours: 'h',
          days: 'd',
          weeks: 'wk',
          months: 'mo',
          years: 'y',
        },
      },
    }
  },

  computed: {
    ...mapGetters(['language', 'organization', 'account']),

    currentCurrency() {
      return this.account?.site?.currency || this.organization?.currency
    },

    impactTitle() {
      return this.metric ? this.$t(`impacts.${this.metric}`) : '-'
    },

    metricText() {
      if (!this.total) return '-'
      if (!this.unit) return this.formatNumber(this.total)

      if (this.unit === this.$constants.metricUnitTypes.currency) {
        return `${this.exchangeCurrency} ${this.currentCurrency}`
      }

      const { total, unit } = this.getMetric()

      // The library that converts to the greatest unit (minutes to weeks for example) always returns the unit in plural, we need to remove the s to properly handle the pluralization and translation with phrase
      const singularUnit = this.removeTrailingS(unit)

      const unitText =
        unit !== null
          ? this.$t(`impact_units_short.${_.snakeCase(singularUnit)}`, {
              count: total,
            })
          : ''

      return `${this.formatNumber(Number(total))} ${unitText}`
    },

    exchangeCurrency() {
      const exchangeRate =
        this.account.site.currency === this.organization.currency
          ? 1
          : this.organization.allowedSiteCurrencies.find(
              c => c.currency === this.currentCurrency
            ).exchangeRateFromGlobal

      const total = this.total * exchangeRate

      return this.formatCurrency(total, this.currentCurrency)
    },
  },

  methods: {
    removeTrailingS(str) {
      if (str.endsWith('s')) {
        return str.slice(0, -1)
      }
      return str
    },

    formatCurrency(value, currency) {
      return new Intl.NumberFormat('en-us', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol', // Display just the symbol do not change
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(value)
    },

    convertSystemUnits(fromSystem, toSystem) {
      const from = this.units[fromSystem]?.[this.unit]
      const toUnit = this.getUnit()

      try {
        const conversion = convert(this.total, from).to(toUnit, toSystem)
        const convertedValue = conversion?.quantity ?? conversion
        const convertedUnit =
          this.getKey(
            { ...this.units.metric, ...this.units.imperial },
            conversion.unit
          ) ?? toUnit
        return { total: convertedValue, unit: convertedUnit }
      } catch (error) {
        return { total: this.total, unit: this.unit }
      }
    },

    convertTimeUnits() {
      try {
        const conversion = convert(this.total, this.unit).to('best')

        let convertedValue = conversion?.quantity
        let convertedUnit = this.getKey({ ...this.units.time }, conversion.unit)

        if (
          (convertedUnit === 'd' || convertedUnit === 'days') &&
          convertedValue >= 7 &&
          convertedValue < 30
        ) {
          const week = convert(this.total, this.unit).to('weeks')

          convertedValue = week
          convertedUnit = this.getKey({ ...this.units.time }, 'wk')
        }

        if (
          (convertedUnit === 'd' || convertedUnit === 'days') &&
          convertedValue >= 30
        ) {
          const month = convert(this.total, this.unit).to('months')

          convertedValue = month
          convertedUnit = this.getKey({ ...this.units.time }, 'mo')
        }

        return { total: convertedValue, unit: convertedUnit }
      } catch (error) {
        console.warn(`No conversion available for ${this.unit}`)
        return { total: this.total, unit: this.unit }
      }
    },

    getUnit() {
      const country = this.account.site?.countryCodes?.[0]

      switch (this.unit) {
        case 'uk_ton':
        case 'us_ton':
          return 'metric ton'
        case 'tonne':
        case 'metric_ton':
          return country === 'uk'
            ? this.units.imperial.uk_ton
            : this.units.imperial.us_ton
        default:
          return 'best'
      }
    },

    getKey(object, value) {
      return Object.keys(object).find(key => object[key] === value)
    },

    getMetric() {
      // Imperial vs Metric system
      if (this.system === 'imperial' && this.units.metric[this.unit]) {
        return this.convertSystemUnits('metric', 'imperial')
      } else if (this.system === 'metric' && this.units.imperial[this.unit]) {
        return this.convertSystemUnits('imperial', 'metric')
      }

      // Time
      if (this.units.time[this.unit]) {
        return this.convertTimeUnits()
      }

      // General/No system
      return { total: this.total, unit: this.unit }
    },

    formatNumber(value) {
      return Intl.NumberFormat('en-us', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    },
  },
}
</script>

<style lang="scss">
.impact-custom-title {
  line-height: 8px;
  letter-spacing: -0.11px;
}

.left-icon {
  border-radius: 2px 0px 0px 2px;
  background: #006796;
  width: 4px;
  height: 8px;
}

.right-icon {
  border-radius: 0px 2px 2px 0px;
  background: #2f9aa9;
  width: 4px;
  height: 8px;
}
</style>
